import React, { useState } from "react";
import { useParams } from "react-router-dom";
import stringify from "json-stringify-pretty-compact";

import dayjs from "dayjs-local";
import { Page } from "modules/layout/Page";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from "components/Table";
import { LinkButton } from "components/LinkButton";
import firebase from "firebaseFactory";
import { useCachedAsyncData, useLiveData } from "hooks";
import { DeviceReference } from "modules/devices";
import { FirebaseShard } from "firebaseWrapper";

import { DeviceStatus } from "../components/DeviceStatus";

export const DeviceDetailsMessageRow = ({
  siteId,
  unitId,
  deviceId,
  messageCategory,
  messageType,
}: DeviceReference & {
  messageCategory: string;
  messageType: string;
}) => {
  const [latestData, setLatestData] = useState<{ timestamp_utc: string }[]>([]);
  useLiveData(
    {
      isPaused: false,
      maxDataPoints: 1,
      firebaseShard: FirebaseShard.Tracing,
      firebaseRef: `devices/dataBySite/${siteId}/historyByDevice/${unitId}/${deviceId}/${messageCategory}/${messageType}`,
      orderByDescending: true,
      onInitialData: setLatestData,
      onIncrementalData: setLatestData,
    },
    [deviceId]
  );
  if (latestData.length === 0) return null;
  return (
    <TableRow>
      <TableCell nowrap>
        <div className="mb-1">
          {messageCategory}/{messageType}
        </div>
        <div className="text-gray-600">
          {!latestData[0].timestamp_utc ? (
            "never"
          ) : (
            <span title={latestData[0].timestamp_utc}>
              {dayjs.utc(latestData[0].timestamp_utc).fromNow()}
            </span>
          )}
        </div>
      </TableCell>
      <TableCell className="">
        <div className="rounded-lg overflow-hidden">
          <div className="bg-gray-800 h-48 p-4 font-mono text-xs text-white overflow-x-scroll whitespace-pre-wrap">
            {stringify(latestData[0], {
              maxLength: JSON.stringify(latestData[0]).length - 1,
            })}
          </div>
        </div>
      </TableCell>
      <TableCell>
        <LinkButton
          className="mb-5 whitespace-no-wrap"
          to={`/sites/${siteId}/units/${unitId}/device/${deviceId}/${messageCategory}/${messageType}`}
        >
          View live stream
        </LinkButton>
        {messageType === "data/log_data" && (
          <LinkButton
            className="whitespace-no-wrap"
            to={`/sites/${siteId}/units/${unitId}/device/${deviceId}/logs`}
          >
            View logs stream
          </LinkButton>
        )}
      </TableCell>
    </TableRow>
  );
};
export const DeviceMessagesPage = () => {
  const { siteId, unitId, deviceId } = useParams();

  const { data: messageCategories } = useCachedAsyncData(
    async () => {
      const messageTypes = await Promise.all(
        ["data", "command", "response", "request"].map(
          async (messageCategory) => {
            const messageTypesInCategory = await firebase
              .database(firebase.shards.Tracing)
              .getKeysAtPath(
                `devices/dataBySite/${siteId}/historyByDevice/${unitId}/${deviceId}/${messageCategory}`
              );
            return messageTypesInCategory.map((messageType) => ({
              messageCategory,
              messageType,
            }));
          }
        )
      );
      return messageTypes.flat();
    },
    [siteId, unitId, deviceId],
    { initialData: [] }
  );

  return (
    <Page
      titleSupplement={
        <DeviceStatus
          siteId={siteId}
          unitId={unitId}
          deviceId={deviceId}
          className="ml-2"
        />
      }
    >
      <Table className="table-fixed">
        <TableHead>
          <TableRow>
            <TableHeaderCell className="w-1/6" nowrap>
              Message Type
            </TableHeaderCell>
            <TableHeaderCell className="w-4/6">Message</TableHeaderCell>
            <TableHeaderCell className="w-1/6" nowrap>
              Actions
            </TableHeaderCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {messageCategories.map(({ messageCategory, messageType }, index) => (
            <DeviceDetailsMessageRow
              key={index}
              siteId={siteId}
              unitId={unitId}
              deviceId={deviceId}
              messageType={messageType}
              messageCategory={messageCategory}
            />
          ))}
          {messageCategories.length === 0 && (
            <TableRow>
              <TableCell nowrap>
                No messages received from this device
              </TableCell>
              <TableCell />
              <TableCell />
            </TableRow>
          )}
        </TableBody>
      </Table>
    </Page>
  );
};
